/**
 * ui.jsx — Shared UI Components
 * Rently.ma Design System
 *
 * All components use CSS variables from index.css.
 * No hardcoded colors — always var(--token).
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ── 1. SkeletonBlock ─────────────────────────────────────────────────
export function SkeletonBlock({ width = '100%', height = 14, radius = 6, style: extra }) {
  return (
    <div
      className="skeleton"
      style={{ width, height, borderRadius: radius, ...extra }}
    />
  );
}

// ── 2. SkeletonCard ──────────────────────────────────────────────────
export function SkeletonCard({ height = 120, width = '100%' }) {
  return (
    <div style={{
      height,
      width,
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: 'var(--space-xl)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-md)',
    }}>
      <SkeletonBlock width="40%" height={12} />
      <SkeletonBlock width="70%" height={10} />
      <SkeletonBlock width="55%" height={10} />
    </div>
  );
}

// ── 3. SkeletonTable ─────────────────────────────────────────────────
export function SkeletonTable({ rows = 5 }) {
  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: 'var(--space-xl)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-md)',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', gap: 'var(--space-lg)', marginBottom: 'var(--space-sm)' }}>
        {[120, 180, 100, 80].map((w, i) => (
          <SkeletonBlock key={i} width={w} height={14} />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--space-lg)', alignItems: 'center' }}>
          {[100, 160, 90, 70].map((w, j) => (
            <SkeletonBlock key={j} width={w} height={12} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Sparkline helper (SVG mini chart for StatCard) ───────────────────
function Sparkline({ data = [], color = 'var(--gold)', width = 80, height = 32 }) {
  const [gradId] = useState(() => 'sg' + Math.random().toString(36).slice(2, 8));

  if (!data || data.length < 2) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const padY = 2;

  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - padY - ((v - min) / range) * (height - padY * 2);
    return [x, y];
  });

  const polylinePoints = pts.map(p => p.join(',')).join(' ');
  const fillPoints = [`0,${height}`, ...pts.map(p => p.join(',')), `${width},${height}`].join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPoints} fill={`url(#${gradId})`} />
      <polyline
        points={polylinePoints}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── 4. StatCard (Premium KPI card — Stripe/Linear pattern) ──────────
export function StatCard({ label, value, trend, trendUp, comparison, sparkData, onClick, index = 0 }) {
  const [hovered, setHovered] = useState(false);

  const trendColor = trendUp === true ? 'var(--green)'
    : trendUp === false ? 'var(--red)'
    : 'var(--text-secondary)';

  const trendArrow = trendUp === true ? '↑' : trendUp === false ? '↓' : '';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--card)',
        border: `1px solid ${hovered ? 'var(--border-gold)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        padding: '20px 24px',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: hovered ? 'var(--shadow-gold-glow)' : 'var(--shadow-card)',
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'all 250ms var(--ease-smooth)',
        animation: 'scaleIn 300ms var(--ease-smooth) both',
        animationDelay: `${index * 50}ms`,
      }}
    >
      {/* Top row: Label + Sparkline */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <span className="stat-label" style={{
          fontSize: 'var(--text-label)',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}>
          {label}
        </span>
        {sparkData && sparkData.length >= 2 && (
          <Sparkline data={sparkData} />
        )}
      </div>

      {/* Value — the hero element */}
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'clamp(1.75rem, 1.5rem + 1vw, 2.25rem)',
        fontWeight: 700,
        color: 'var(--text)',
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        marginBottom: 4,
      }}>
        {value}
      </div>

      {/* Trend line */}
      {trend ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: trendColor,
          }}>
            {trendArrow && `${trendArrow} `}{trend}
          </span>
          {comparison && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {comparison}
            </span>
          )}
        </div>
      ) : (
        <div style={{ height: '0.875rem' }} />
      )}
    </div>
  );
}

// ── 5. PageHeader ────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 'var(--space-xl)',
      animation: 'fadeInUp 400ms var(--ease-spring) both',
    }}>
      <div>
        <h1 style={{
          fontSize: 'var(--text-heading)',
          fontWeight: 600,
          color: 'var(--text)',
          letterSpacing: '-0.01em',
        }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{
            fontSize: 'var(--text-body)',
            color: 'var(--text-secondary)',
            marginTop: 'var(--space-xs)',
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── 6. Badge ─────────────────────────────────────────────────────────
const BADGE_VARIANTS = {
  success:  { bg: 'var(--status-available-bg)',    color: 'var(--status-available)' },
  warning:  { bg: 'var(--status-maintenance-bg)',  color: 'var(--status-maintenance)' },
  danger:   { bg: 'var(--status-alert-bg)',        color: 'var(--status-alert)' },
  info:     { bg: 'var(--status-rented-bg)',        color: 'var(--status-rented)' },
  neutral:  { bg: 'var(--status-neutral-bg)',       color: 'var(--status-neutral)' },
};

export function Badge({ label, variant = 'neutral' }) {
  const v = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      fontSize: 'var(--text-label)',
      fontWeight: 600,
      borderRadius: 20,
      background: v.bg,
      color: v.color,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── 7. EmptyState ────────────────────────────────────────────────────
export function EmptyState({ icon = null, title, description, ctaLabel, onCta }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-3xl) var(--space-2xl)',
      textAlign: 'center',
      animation: 'fadeInUp 400ms var(--ease-spring) both',
    }}>
      {icon && <div style={{ fontSize: 48, marginBottom: 'var(--space-lg)' }}>{icon}</div>}
      <div style={{
        fontSize: 'var(--text-title)',
        fontWeight: 600,
        color: 'var(--text)',
        marginBottom: 'var(--space-sm)',
      }}>
        {title}
      </div>
      {description && (
        <div style={{
          fontSize: 'var(--text-body)',
          color: 'var(--text-secondary)',
          maxWidth: 360,
          marginBottom: ctaLabel ? 'var(--space-xl)' : 0,
        }}>
          {description}
        </div>
      )}
      {ctaLabel && onCta && (
        <button
          onClick={onCta}
          style={{
            padding: '10px 24px',
            height: 44,
            background: 'var(--gold)',
            color: 'var(--bg)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.02em',
          }}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

// ── 8. SearchInput ───────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder = '', style: extraStyle }) {
  const [local, setLocal] = useState(value || '');
  const [focused, setFocused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setLocal(value || '');
  }, [value]);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setLocal(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange?.(val);
    }, 300);
  }, [onChange]);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div style={{ position: 'relative', ...extraStyle }}>
      <svg
        style={{
          position: 'absolute',
          left: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: focused ? 'var(--gold)' : 'var(--text-muted)',
          transition: `color var(--duration-base) var(--ease-smooth)`,
        }}
        width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="text"
        value={local}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        style={{
          width: '100%',
          height: 44,
          paddingLeft: 38,
          paddingRight: 12,
          background: 'var(--card)',
          border: `1px solid ${focused ? 'var(--gold)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text)',
          fontSize: 'var(--text-body)',
          outline: 'none',
          fontFamily: 'inherit',
          boxShadow: focused ? 'var(--shadow-focus)' : 'none',
          transition: `border-color var(--duration-base) var(--ease-smooth),
                       box-shadow var(--duration-base) var(--ease-smooth)`,
        }}
      />
    </div>
  );
}

// ── 9. CommandPalette ────────────────────────────────────────────────
const kbdStyle = {
  display: 'inline-block',
  padding: '1px 5px',
  background: 'var(--card)',
  border: '1px solid var(--border-strong)',
  borderRadius: 4,
  fontSize: 10,
  fontFamily: 'inherit',
  marginRight: 4,
};

export function CommandPalette({ items = [], isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onClose?.(!isOpen);
      }
      if (e.key === 'Escape' && isOpen) {
        onClose?.(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const filtered = items.filter(item =>
    item.label.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      e.preventDefault();
      filtered[selectedIndex].action?.();
      onClose?.(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={() => onClose?.(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '20vh',
        zIndex: 9999,
        animation: `fadeIn var(--duration-fast) var(--ease-smooth)`,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass"
        style={{
          width: 480,
          maxWidth: '90vw',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
          animation: `scaleIn var(--duration-base) var(--ease-smooth)`,
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        <div style={{ padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)' }}>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher une commande…"
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: 'var(--text)',
              fontSize: 15,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: 'var(--space-sm) 0' }}>
          {filtered.length === 0 && (
            <div style={{ padding: 'var(--space-lg)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-body)' }}>
              Aucun résultat
            </div>
          )}
          {filtered.map((item, i) => (
            <div
              key={i}
              onClick={() => { item.action?.(); onClose?.(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-md)',
                padding: '10px var(--space-lg)',
                cursor: 'pointer',
                background: i === selectedIndex ? 'var(--raised)' : 'transparent',
                color: i === selectedIndex ? 'var(--text)' : 'var(--text-secondary)',
                transition: `background var(--duration-fast) var(--ease-smooth)`,
              }}
            >
              {item.icon && <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.icon}</span>}
              <span style={{ fontSize: 'var(--text-body)' }}>{item.label}</span>
            </div>
          ))}
        </div>
        <div style={{
          padding: 'var(--space-sm) var(--space-lg)',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 'var(--space-lg)',
          fontSize: 'var(--text-label)',
          color: 'var(--text-muted)',
        }}>
          <span><kbd style={kbdStyle}>↑↓</kbd> navigate</span>
          <span><kbd style={kbdStyle}>↵</kbd> select</span>
          <span><kbd style={kbdStyle}>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

// ── 10. NotificationBadge ────────────────────────────────────────────
export function NotificationBadge({ count = 0 }) {
  if (count <= 0) return null;

  const display = count > 99 ? '99+' : String(count);

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      background: 'var(--red)',
      color: 'var(--bg)',
      fontSize: 10,
      fontWeight: 700,
      borderRadius: 10,
      lineHeight: '18px',
    }}>
      {display}
    </span>
  );
}

// ── 11. GlassModal ──────────────────────────────────────────────────
export function GlassModal({ open, onClose, title, subtitle, width = 480, children }) {
  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn var(--duration-fast) var(--ease-smooth)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--card)',
          border: '1px solid var(--border-medium)',
          borderRadius: 'var(--radius)',
          animation: 'scaleIn var(--duration-base) var(--ease-smooth)',
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        {/* Header */}
        {title && (
          <div style={{
            padding: 'var(--space-xl)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)' }}>
                {title}
              </div>
              {subtitle && (
                <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 2 }}>
                  {subtitle}
                </div>
              )}
            </div>
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none', color: 'var(--text-muted)',
              fontSize: 20, cursor: 'pointer', padding: 4, lineHeight: 1,
            }}>×</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// ── 12. Card ─────────────────────────────────────────────────────────
export function Card({ children, hoverable = false, style: extraStyle, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={hoverable ? () => setHovered(true) : undefined}
      onMouseLeave={hoverable ? () => setHovered(false) : undefined}
      style={{
        background: 'var(--card)',
        border: `1px solid ${hovered ? 'var(--border-gold)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        padding: 'var(--space-xl)',
        boxShadow: hovered ? 'var(--shadow-gold-glow)' : 'var(--shadow-card)',
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
        transition: hoverable
          ? `border-color var(--duration-base) var(--ease-smooth),
             box-shadow var(--duration-base) var(--ease-smooth),
             transform var(--duration-base) var(--ease-smooth)`
          : undefined,
        cursor: onClick ? 'pointer' : undefined,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
        ...extraStyle,
      }}
    >
      {children}
    </div>
  );
}
